import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";

export default function ProductImages({ images, setImages, onDeleteExistingImage }) {
  const { dashboardTheme } = useDashboardTheme();
  const fileRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const sectionSx = {
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    p: { xs: 1.5, md: 2 },
  };

  const getImageSrc = (image) => image.preview || image.image || "";

  const openPicker = () => {
    fileRef.current?.click();
  };

  const showError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const onFilesSelected = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const validFiles = [];

    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        showError("Solo se permiten imágenes JPG, PNG o WEBP.");
        continue;
      }

      const preview = URL.createObjectURL(file);

      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview,
        name: file.name,
        existing: false,
      });
    }

    if (validFiles.length) {
      setImages((prev) => [...prev, ...validFiles]);
    }

    event.target.value = "";
  };

  const removeImage = async (imageId) => {
    const image = images.find((item) => item.id === imageId);

    if (image?.existing && onDeleteExistingImage) {
      await onDeleteExistingImage(image);
      if (selectedImage?.id === imageId) {
        setSelectedImage(null);
        setPreviewOpen(false);
      }
      return;
    }

    setImages((prev) => {
      const imageToRemove = prev.find((item) => item.id === imageId);

      if (imageToRemove?.preview && !imageToRemove.existing) {
        URL.revokeObjectURL(imageToRemove.preview);
      }

      return prev.filter((item) => item.id !== imageId);
    });

    if (selectedImage?.id === imageId) {
      setSelectedImage(null);
      setPreviewOpen(false);
    }
  };

  const openPreview = (image) => {
    setSelectedImage(image);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  useEffect(() => {
    return () => {
      images.forEach((item) => {
        if (item.preview && !item.existing) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, []);

  return (
    <Paper elevation={0} sx={sectionSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 19, md: 21 },
            fontWeight: 600,
            color: dashboardTheme.text_color,
          }}
        >
          Imágenes
        </Typography>

        <Button
          variant="outlined"
          startIcon={<AddPhotoAlternateRoundedIcon />}
          onClick={openPicker}
          sx={outlinedButtonSx}
        >
          Añadir imágenes
        </Button>

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          hidden
          onChange={onFilesSelected}
        />
      </Box>

      {images.length === 0 ? (
        <Box
          sx={{
            minHeight: 130,
            borderRadius: "12px",
            border: `1px dashed ${dashboardTheme.border_color}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            color: dashboardTheme.muted_text_color,
            bgcolor:
              dashboardTheme.theme_mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(17,24,39,0.02)",
          }}
        >
          <ImageRoundedIcon sx={{ fontSize: 34 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            No hay imágenes seleccionadas
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(6, minmax(0, 1fr))",
            },
            gap: 1.2,
          }}
        >
          {images.map((image) => (
            <Paper
              key={image.id}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: `1px solid ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.table_background,
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={getImageSrc(image)}
                alt={image.name}
                sx={{
                  width: "100%",
                  height: 110,
                  objectFit: "cover",
                  display: "block",
                  bgcolor: dashboardTheme.input_background,
                }}
              />

              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  p: 0.7,
                  justifyContent: "space-between",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => openPreview(image)}
                  sx={{
                    color: dashboardTheme.primary_color,
                  }}
                >
                  <VisibilityRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => removeImage(image.id)}
                  sx={{
                    color: "#dc2626",
                  }}
                >
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            borderBottom: `1px solid ${dashboardTheme.border_color}`,
          }}
        >
          <IconButton onClick={closePreview} sx={{ color: dashboardTheme.text_color }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          {selectedImage && (
            <Box
              component="img"
              src={getImageSrc(selectedImage)}
              alt={selectedImage.name}
              sx={{
                width: "100%",
                maxHeight: "72vh",
                objectFit: "contain",
                display: "block",
                borderRadius: "12px",
                bgcolor: dashboardTheme.input_background,
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            py: 3,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#D32F2F",
              fontSize: 17,
              fontWeight: 600,
              mb: 2,
            }}
          >
            {errorMessage}
          </Typography>

          <Button
            variant="contained"
            onClick={() => setErrorOpen(false)}
            sx={dashboardTheme.buttonStyles.contained}
          >
            Entendido
          </Button>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}